import React, { useState } from "react";
import { toast } from "sonner";
import { Gift, ArrowUpRight } from "lucide-react";
import { apiFetch, safeJson } from "@/lib/api";
import { CLOUDBET_REFERRAL_URL } from "@/constants/links";

export default function AccessGate({ onAccessGranted }) {
  const [code, setCode] = useState("");
  const [adminPin, setAdminPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [adminMode, setAdminMode] = useState(false);

  const submitCode = async () => {
    if (!code.trim()) { setError("Please enter an access code"); return; }
    setLoading(true); setError("");
    try {
      const res = await apiFetch("/api/auth/verify-code", {
        method: "POST",
        body: JSON.stringify({ code: code.trim(), device_name: "Web" }),
      });
      const data = await safeJson(res);
      if (data && res.ok && data.success) {
        toast.success(data.message || "Access granted");
        onAccessGranted(data.token, false, data.profile_complete || false);
      } else {
        setError((data && data.detail) || "Invalid or expired code");
      }
    } catch {
      setError("Unable to connect to server. Please try again.");
    } finally { setLoading(false); }
  };

  const submitAdmin = async () => {
    if (!adminPin.trim()) { setError("Please enter admin PIN"); return; }
    setLoading(true); setError("");
    try {
      const res = await apiFetch("/api/auth/admin-login", {
        method: "POST",
        body: JSON.stringify({ admin_pin: adminPin.trim() }),
      });
      const data = await safeJson(res);
      if (data && res.ok && data.success) {
        toast.success("Admin access granted");
        onAccessGranted(data.token, true, true);
      } else {
        setError((data && data.detail) || "Invalid admin PIN");
      }
    } catch {
      setError("Unable to connect to server. Please try again.");
    } finally { setLoading(false); }
  };

  const onSubmit = adminMode ? submitAdmin : submitCode;

  return (
    <div className="min-h-screen bg-black flex flex-col">
      {/* Logo */}
      <div className="px-6 pt-6 pb-2">
        <span style={{ fontSize: 16, letterSpacing: 6, fontWeight: 800 }}>
          <span className="text-white">BREAK</span>
          <span style={{ color: "#c8ff00" }}>POINT</span>
          <span style={{ color: "#c8ff00", fontSize: 12 }}> ■</span>
        </span>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col justify-center px-6 max-w-md w-full mx-auto">
        <p className="text-[12px] text-[#555] tracking-[4px] font-semibold mb-3 uppercase">
          {adminMode ? "§ADMIN" : "§BREAKPOINT"}
        </p>
        <h1 className="text-[42px] leading-none font-black text-white mb-2">
          {adminMode ? "Admin." : "Enter code."}
        </h1>
        <p className="text-[12px] text-[#666] tracking-[3px] font-medium mb-8 uppercase">
          {adminMode ? "PIN Authentication" : "Invite code required"}
        </p>

        <label className="text-[11px] text-[#888] tracking-[3px] font-semibold mb-2 block">
          {adminMode ? "PIN" : "CODE"}
        </label>
        {adminMode ? (
          <input
            type="password"
            inputMode="numeric"
            maxLength={8}
            value={adminPin}
            onChange={(e) => { setAdminPin(e.target.value); setError(""); }}
            onKeyDown={(e) => e.key === "Enter" && onSubmit()}
            placeholder="••••"
            data-testid="admin-pin-input"
            className="w-full bg-black border border-[#333] rounded-lg px-4 py-4 text-white text-lg tracking-[4px] font-medium outline-none focus:border-[#c8ff00] font-mono"
            autoFocus
          />
        ) : (
          <input
            value={code}
            onChange={(e) => { setCode(e.target.value.toUpperCase()); setError(""); }}
            onKeyDown={(e) => e.key === "Enter" && onSubmit()}
            placeholder="XXXXXXXX"
            maxLength={12}
            data-testid="access-code-input"
            className="w-full bg-black border border-[#333] rounded-lg px-4 py-4 text-white text-lg tracking-[4px] font-medium outline-none focus:border-[#c8ff00] font-mono"
            autoFocus
          />
        )}

        {error ? <p className="text-[#ff4444] text-xs tracking-wide mt-3" data-testid="access-error">{error}</p> : null}

        <button
          onClick={onSubmit}
          disabled={loading}
          data-testid="access-submit-btn"
          className="w-full mt-4 bg-[#c8ff00] text-black font-extrabold tracking-[3px] py-4 rounded-lg disabled:opacity-50"
        >
          {loading ? "…" : adminMode ? "AUTHENTICATE →" : "VERIFY CODE →"}
        </button>

        <div className="flex items-center my-5">
          <div className="flex-1 h-px bg-[#222]" />
          <span className="text-[#555] text-[11px] tracking-[2px] mx-4">OR</span>
          <div className="flex-1 h-px bg-[#222]" />
        </div>

        <button
          onClick={() => { setAdminMode(!adminMode); setError(""); }}
          data-testid="toggle-admin-mode"
          className="w-full border border-[#333] text-white text-xs font-bold tracking-[3px] py-3.5 rounded-lg"
        >
          {adminMode ? "← BACK TO INVITE CODE" : "I'M THE ADMIN"}
        </button>

        {!adminMode && (
          <a
            href={CLOUDBET_REFERRAL_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="cloudbet-referral-link"
            className="flex items-center gap-3 mt-5 border border-[#c8ff00]/30 bg-[#c8ff00]/[0.06] hover:bg-[#c8ff00]/[0.12] transition-colors rounded-lg px-4 py-3.5"
          >
            <Gift size={18} style={{ color: "#c8ff00" }} className="shrink-0" />
            <div className="flex-1">
              <p className="text-white text-[12px] font-bold tracking-[1px]">NEW TO CLOUDBET?</p>
              <p className="text-[#888] text-[11px] tracking-[1px]">Create your account with our partner link</p>
            </div>
            <ArrowUpRight size={16} style={{ color: "#c8ff00" }} className="shrink-0" />
          </a>
        )}
      </div>

      <div className="px-6 pb-6">
        <p className="text-[#333] text-[10px] tracking-[3px] font-semibold">AUTOMATED TRADING · CLOUDBET · 24/7</p>
      </div>
    </div>
  );
}
